/**
 * Measure how long content was actually on screen and in the foreground.
 *
 * A timer in a WebView keeps running when the app is backgrounded. Left alone, "time
 * spent" is inflated by however long the phone sat in a pocket — sometimes by hours
 * — which makes the metric worse than not having it. So the clock stops on
 * background and restarts on foreground.
 *
 * Time is reported in **increments, not totals**, each tagged with a `view_id`. An
 * article read across three foregroundings is three events that the backend sums per
 * visit. Reporting a running total instead would either triple-count the reading or
 * lose two thirds of it, depending on which event arrived last.
 */

import { useEffect, useRef } from "react";

import { isAppActive, onAppStateChange } from "~/lib/app-state";

export type DwellReason = "navigate" | "background";

interface Options {
  key: string | null;
  /** Called with the milliseconds accumulated *since the last call*. */
  onSettle: (milliseconds: number, viewId: string, reason: DwellReason) => void;
  /**
   * Readings below this are discarded. It exists to drop bounces, and it also
   * swallows StrictMode's sub-millisecond mount/cleanup/mount round trip — one
   * mechanism doing two jobs.
   */
  minMs?: number;
}

export function useDwellTime({ key, onSettle, minMs = 1_000 }: Options): void {
  const state = useRef({ accumulated: 0, startedAt: 0, viewId: "" });
  const latest = useRef(onSettle);
  useEffect(() => {
    latest.current = onSettle;
  });

  useEffect(() => {
    if (key === null) return;

    const s = state.current;
    s.accumulated = 0;
    s.viewId = crypto.randomUUID();
    s.startedAt = isAppActive() ? Date.now() : 0;

    const stop = () => {
      if (s.startedAt === 0) return;
      s.accumulated += Date.now() - s.startedAt;
      s.startedAt = 0;
    };
    const start = () => {
      if (s.startedAt === 0) s.startedAt = Date.now();
    };
    const emit = (reason: DwellReason) => {
      if (s.accumulated < minMs) return;
      const milliseconds = s.accumulated;
      // Reported, so never counted again.
      s.accumulated = 0;
      latest.current(milliseconds, s.viewId, reason);
    };

    const unsubscribe = onAppStateChange((active) => {
      if (active) {
        start();
        return;
      }
      stop();
      // Report immediately: a backgrounded app may be killed without ever running
      // this effect's cleanup.
      emit("background");
    });

    return () => {
      unsubscribe();
      stop();
      emit("navigate");
    };
  }, [key, minMs]);
}
